import PageHeader from "components/common/PageHeader";
import DetailsShimmer from "components/common/shimmer/DetailsShimmer";
import InfoItem from "components/common/detailsPage/InfoItem";
import NoPermission from "components/common/NoPermission";
import { SmsCampaignStatusBadge } from "components/common/StatusBadge";
import DataTable from "components/common/table/DataTable";
import { usePermission } from "hooks/usePermission";
import { useQueryParam } from "hooks/useQueryParam";
import React, { useEffect, useMemo } from "react";
import { Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { getSmsHistoryDetails } from "store/slices/smsHistorySlice";
import { formatUTCDate } from "utils/utils";

const SmsCampaignDetails = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const id = useQueryParam("id");

  const { canAction } = usePermission();
  const { canView } = canAction("send_sms");

  const { loading, itemDetails } = useSelector((state) => state.SmsHistory);
  const {
    message,
    type,
    customers,
    status,
    total,
    sent,
    failed,
    error,
    createdBy,
    createdAt,
    startedAt,
    completedAt,
  } = itemDetails || {};

  useEffect(() => {
    if (id && canView) {
      dispatch(getSmsHistoryDetails(id));
    }
  }, [id, canView, dispatch]);

  const infoFields = useMemo(
    () => [
      {
        label: t("Status"),
        render: <SmsCampaignStatusBadge status={status} t={t} />,
        col: { lg: 3, md: 3, sm: 6, xs: 6 },
      },
      {
        label: t("Send To"),
        value:
          type === "specific_customers"
            ? t("Specific Customers")
            : t("All Customers"),
        col: { lg: 3, md: 3, sm: 6, xs: 6 },
      },
      {
        label: t("Sent By"),
        value: createdBy?.name || "-",
        col: { lg: 3, md: 3, sm: 6, xs: 6 },
      },
      {
        label: t("Date Created"),
        value: createdAt ? formatUTCDate(createdAt) : "-",
        col: { lg: 3, md: 3, sm: 6, xs: 6 },
      },
      { isSpacer: true },
      {
        label: t("Total Recipients"),
        value: total ?? "-",
        col: { lg: 3, md: 3, sm: 6, xs: 6 },
      },
      {
        label: t("Sent"),
        render: <h6 className="value text-success mb-0">{sent ?? 0}</h6>,
        col: { lg: 3, md: 3, sm: 6, xs: 6 },
      },
      {
        label: t("Failed"),
        render: (
          <h6
            className={`value mb-0 ${failed > 0 ? "text-danger" : ""}`}
          >
            {failed ?? 0}
          </h6>
        ),
        col: { lg: 3, md: 3, sm: 6, xs: 6 },
      },
      {
        label: t("Completed At"),
        value: completedAt ? formatUTCDate(completedAt) : "-",
        col: { lg: 3, md: 3, sm: 6, xs: 6 },
      },
      ...(startedAt
        ? [
            { isSpacer: true },
            {
              label: t("Started At"),
              value: formatUTCDate(startedAt),
              col: { lg: 3, md: 3, sm: 6, xs: 6 },
            },
          ]
        : []),
      ...(error
        ? [
            { isSpacer: true },
            {
              label: t("Error"),
              render: <h6 className="value text-danger mb-0">{error}</h6>,
              col: { lg: 12, md: 12 },
            },
          ]
        : []),
      { isSpacer: true },
      {
        label: t("Message"),
        value: message || "-",
        col: { lg: 12, md: 12 },
      },
    ],
    [
      t,
      status,
      type,
      createdBy,
      createdAt,
      total,
      sent,
      failed,
      completedAt,
      startedAt,
      error,
      message,
    ],
  );

  const recipientColumns = useMemo(
    () => [
      {
        header: t("Name"),
        key: "name",
        render: (value) => value || "-",
      },
      {
        header: t("Phone Number"),
        key: "phone",
        render: (value) => <div className="ltr_text">{value || "-"}</div>,
      },
    ],
    [t],
  );

  const shimmerLayout = [
    {
      col: 12,
      blocks: [
        { rows: 1, cols: 6 },
        { rows: 1, cols: 5 },
      ],
    },
    {
      col: 12,
      blocks: [{ rows: 1, cols: 3 }],
    },
  ];

  if (!canView) return <NoPermission />;

  if (loading) {
    return (
      <div className="page-content">
        <PageHeader
          title={t("SMS Campaign Details")}
          isBackBtn
          className="details_page_title"
        />
        <DetailsShimmer layout={shimmerLayout} />
      </div>
    );
  }

  return (
    <div className="page-content common_details_page sms_campaign_details_page">
      <PageHeader
        title={t("SMS Campaign Details")}
        isBackBtn
        className="details_page_title"
      />
      <div className="common_card mb-4">
        <h2 className="title_20 mb-4">{t("General Details")}</h2>
        <Row>
          {infoFields.map((item, index) => {
            if (item.isSpacer) {
              return <div key={index} className="mt-4 w-100" />;
            }

            return (
              <InfoItem
                key={index}
                label={item.label}
                value={item.value}
                col={item.col}
              >
                {item.render}
              </InfoItem>
            );
          })}
        </Row>
      </div>

      {Array.isArray(customers) && customers.length > 0 && (
        <div className="common_card">
          <h2 className="title_20 mb-4">
            {t("Recipients")} ({customers.length})
          </h2>
          <DataTable columns={recipientColumns} data={customers} t={t} />
        </div>
      )}
    </div>
  );
};

export default SmsCampaignDetails;
